import { useMemo, useState } from 'react'

import { Divider, message, notification } from 'antd'
import { useSelector } from 'react-redux'
import { isFunction, snakeCase } from 'lodash'

import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { isValid } from '../../../utils/validators'
import { isNonEmptyArray, isObject } from '../../../utils'
import { Quota, QuotaValidators } from '../../../utils/entities/quota'
import QuotaForm, { QuotaFormModes } from './QuotaForm'
import FormFooter from '../../../Components/antd/Forms/FormFooter'

import '../../../assets/quotas.scss'

export const BODY_STYLE = { display: 'flex', flexDirection: 'column', height: '93%', width: '100%' }
export const SELECT_STYLE = { width: '200px' }

message.config({
  top: 20,
  duration: 3,
  maxCount: 1,
  placement: 'topRight'
})

const SingleQuotaForm = ({ data, disabled, disabledSector, mode, sectors = [], sectionGroups = {}, studentPathways = [], validators = QuotaValidators, onDelete, onSubmit, onCancel, dividerStyle }) => {
  const t = useSelector(selectTranslate)

  const [formData, setFormData] = useState(new Quota(isObject(data) ? data : { total: 0 }))

  const handleSubmit = event => {
    const errors = isValid(formData, Object.values(validators))

    if (errors.keys.length > 0) {
      const errorMessages = t('common_form.error_fields', {
        fields: errors.keys.map(key => t('common.' + snakeCase(key))).join(', ')
      })

      notification.error({ message: errorMessages })
    } else if (formData.startDate > formData.endDate) {
      message.error({ message: t('common_form.error_dates') })
    } else {
      onSubmit(new Quota(formData))
    }
  }

  const isSubmitDisabled = useMemo(() => (
    !formData.school || formData.total === null || !formData.startDate || !formData.endDate || disabled || !formData.sector
  ), [formData.school, formData.total, formData.startDate, formData.endDate, formData.sector, disabled])

  const notRecommanded = useMemo(() => {
    const base = mode === QuotaFormModes.DESIDERATA && isObject(studentPathways) && isNonEmptyArray(studentPathways[formData.sector?.id])

    return {
      section: base && isObject(formData.section) && !studentPathways[formData.sector.id].some(sp => sp.sectionToYear.section.id === formData.section.id),
      year: base && isObject(formData.sectionToYear) && !studentPathways[formData.sector.id].some(sp => sp.sectionToYear.id === formData.sectionToYear.id)
    }
  }, [formData.section, formData.sectionToYear, mode, studentPathways])
  const submitProps = useMemo(() => {
    const props = {
      text: t('modal.ok_button.save'),
      disabled: isSubmitDisabled,
      title: isSubmitDisabled && t('form_modal.add_button.disabled_tooltip', {
        fields: [
          t('common.period'),
          t('quota_form.places_to_allocate'),
          t('common.school')
        ].join(', ')
      })
    }

    if ((notRecommanded.section || notRecommanded.year) && !disabled) {
      props.popover = {
        content: t(notRecommanded.section ? 'autonomous_quota_form.not_recommanded_section' : 'autonomous_quota_form.not_recommanded_year'),
        title: t('common.warning.!')
      }
    }

    return props
  }, [isSubmitDisabled, notRecommanded, disabled, t])

  return (
    <div>
      <div className='flex-column w-100 pd-12'>
        <QuotaForm
          data={formData}
          disabled={disabled}
          disabledSector={disabledSector}
          mode={mode}
          sectors={sectors}
          sectionGroups={sectionGroups}
          studentPathways={studentPathways}
          onChange={setFormData}
        />
      </div>

      <Divider className='divider-4' />

      <FormFooter
        cancel={{ text: t('modal.cancel_button'), disabled }}
        remove={formData.id > 0 && { popover: { content: t('quota_form.delete_quota'), title: t('common.delete') } }}
        submit={submitProps}
        onCancel={onCancel}
        onDelete={isFunction(onDelete) ? onDelete : null}
        onSubmit={handleSubmit}
      />
    </div>
  )
}

export default SingleQuotaForm
